//================================================================
//  Component: Issue Form
//================================================================

//  Purpose: Form for Reporting an Issue

//  Properties:
//    - setPageStatus = {Function to set the page status after submission}
//    - portals = {Array of portal names to select from}

//  Example:
//    <Issue
//      setPageStatus={setPageStatus}
//      portals={['Digital Services', 'Invertory', 'Demand Management']}
//    ></Issue>    

//================================================================

//Libraries
import React, { useContext } from 'react';
import { useForm } from 'react-hook-form';

//Contexts
import { GetUser } from '../../../Library/GlobalContexts';

//Components
import MultipleScreenshotInput from '../Components/MultipleScreenshotInput';

//Functions
import WriteDocument from '../../../Library/WriteDocument';
import UploadFile from '../../../Library/UploadFile';

//Images

//CSS

export default function Issue({
    setPageStatus,
    portals,
}) {

    //------------------------------------------------------
    //  useContexts & React Router
    //------------------------------------------------------

    const getUser = useContext(GetUser);

    //------------------------------------------------------
    //  useState
    //------------------------------------------------------

    // State to manage loading status
    const [loading, setLoading] = React.useState(false);

    // --------------------------------------------------
    //  Form State
    // --------------------------------------------------

    const { register, handleSubmit, setError, control, clearErrors, reset, formState: { errors } } = useForm();

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    //  Submit Handler
    const onSubmit = (data) => {
        setLoading(true);

        // Generate a unique case ID based on the current timestamp
        const caseid = `inc-${Date.now()}`;

        // Get the screenshot files from the form data
        const screenshotFiles = data?.screenshot;

        let uploadPromises = [];

        if (Array.isArray(screenshotFiles) && screenshotFiles.length > 0) {

            // Map over the screenshot files to create upload promises
            uploadPromises = screenshotFiles.map(file => {
                if (file instanceof File) {
                    const extension = file.name.split('.').pop();
                    const fileid = `file-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
                    const path = `support/${caseid}/${fileid}.${extension}`;

                    return UploadFile(path, file).then((fileurl) => ({
                        fileid,
                        filename: file.name,
                        fileurl,
                        size: file.size,
                        type: file.type,
                    }));
                }
                return Promise.resolve(null);
            });
        }

        return Promise.all(uploadPromises)
            .then((screenshotMetaArray) => {
                const validScreenshotMeta = screenshotMetaArray.filter(meta => meta !== null);

                const support = {
                    caseid,
                    casetype: 'Issue',
                    portal: data.portal || '',
                    description: data.description || '',
                    link: data.link || '',
                    permissionlevel: '',
                    createddate: new Date(),
                    createdby: {
                        email: getUser?.emailaddress,
                        givenname: getUser?.givenname,
                        surname: getUser?.surname,
                    },
                    screenshot: validScreenshotMeta.length > 0 ? validScreenshotMeta : [],
                    problemstatement: '',
                    expectedoutcome: '',
                    businessimpact: '',
                    supportingdocs: [],
                };

                return WriteDocument('support', caseid, support, true);
            })
            .then(() => {
                setLoading(false);
                setPageStatus('success');
                reset();
            })
            .catch((error) => {
                setLoading(false);
                console.error("onSubmit: Error during submission or file upload:", error);
                setPageStatus('error');
                reset();
            });
    };


    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <form className='flex flex-col gap-2 w-full' onSubmit={handleSubmit(onSubmit)}>

            <div className='bg-white rounded-[10px] p-3 md:p-4 shadow-md border-solid border-1 border-neutral-300 h-full '>
                <div className='md:px-3'>

                    {/* ============================ */}
                    {/*          Questions           */}
                    {/* ============================ */}

                    {/*==================1. Which portal does this relate to? *=================*/}
                    <div className='Form-Row'>
                        <label className='font-medium'> 1. Which portal does this relate to? <span className='text-[#C4314B]'>*</span> </label>
                        <select
                            className={errors.portal ? ('Input-Field-Select-Error  md:w-1/2') : ('Input-Field-Select  md:w-1/2')}
                            {...register('portal', {
                                required: 'This field is required',
                            })}
                        >
                            <option hidden value=''>Select one</option>
                            {portals && portals.map((portal, index) => (
                                <option key={index} value={portal}>
                                    {portal}
                                </option>
                            ))}
                        </select>
                        {errors.portal && <p className='font-medium text-sm text-[#DE0000] my-2 mb-0'>{errors.portal?.message}</p>}
                    </div>

                    {/*==================2. Provide a brief description of what you were trying to do =================*/}
                    <div className='Form-Row'>
                        <label className='font-medium'> 2. Provide a brief description of what you were trying to do <span className='text-[#C4314B]'>*</span> </label>
                        <textarea
                            className={errors.description ? ('Input-Field-TextArea-Error md:w-1/2') : ('Input-Field-TextArea md:w-1/2')}
                            {...register('description', {
                                required: 'This field is required',
                            })}
                            placeholder='Describe the problem'
                            autoComplete='off'
                        ></textarea>

                        {errors.description && <p className='font-medium text-sm text-[#DE0000] my-2 mb-0'>{errors.description?.message}</p>}
                    </div>

                    {/*==================3. Provide a link to the page you were trying to access =================*/}
                    <div className='Form-Row'>
                        <label className='font-medium'>
                            3. Provide a link to the page you were trying to access
                        </label>

                        <input
                            className={errors.link ? 'Input-Field-Text-Error !w-full md:!w-1/2' : 'Input-Field-Text w-full md:w-1/2'}
                            {...register('link', {
                                pattern: {
                                    value: /^(https?:\/\/)(localhost|\d{1,3}(\.\d{1,3}){3}|[\w.-]+)(:\d+)?(\/[^\s]*)?$/i,
                                    message: 'Please enter a valid URL starting with http:// or https://',
                                },
                            })}
                            placeholder='https://'
                            type='text'
                            autoComplete='off'
                        ></input>


                        {errors.link && (
                            <p className='font-medium text-sm text-[#DE0000] my-2 mb-0'>
                                {errors.link.message}
                            </p>
                        )}
                    </div>

                    {/* ========== 4. Provide screenshot(s) of the error ========== */}

                    <MultipleScreenshotInput
                        control={control}
                        setError={setError}
                        clearErrors={clearErrors}
                    ></MultipleScreenshotInput>

                </div>
            </div>

            {/* ===================================== */}
            {/*  Submit Button                        */}
            {/* ===================================== */}
            <div>
                <button className='Primary-Button mt-4 max-md:w-full' type='submit' value='submit' disabled={loading}>{loading ? 'Submitting...' : 'Submit'}</button>
            </div>
        </form>
    )
}